import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "@/App";
import { resolveMediaUrl } from "@/lib/media-url";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Code,
  MagnifyingGlass,
  Trophy,
  ChatCircle,
  Heart,
  Eye,
  MusicNote,
  PushPin,
  Star,
  ShareNetwork,
} from "@phosphor-icons/react";

const PAGE_SIZE = 20;

function formatDate(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) {
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours === 0) {
      const mins = Math.floor(diff / (1000 * 60));
      return mins <= 1 ? "Just now" : `${mins}m ago`;
    }
    return `${hours}h ago`;
  }
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const CATEGORY_BADGES = {
  video: { label: "VIDEO", className: "bg-danger/15 text-danger" },
  audio: { label: "SONG", className: "bg-warning/20 text-warning" },
  image: { label: "IMAGE", className: "bg-primary/10 text-primary" },
  html: { label: "HTML", className: "bg-accent/15 text-accent" },
  code: { label: "CODE", className: "bg-muted text-muted-foreground" },
  text: { label: "POST", className: "bg-surface-elevated text-muted-foreground" },
};

function MediaPreview({ post }) {
  const firstMedia = post.media_urls?.[0];
  if (!firstMedia) {
    if (post.category === "html") {
      return (
        <div className="px-4 pb-3">
          <div className="rounded-lg border border-border/30 bg-gradient-to-br from-accent/10 to-primary/10 h-28 flex flex-col items-center justify-center gap-1">
            <Code className="w-7 h-7 text-accent" />
            <span className="text-xs font-medium text-accent">LIVE WEBSITE PREVIEW</span>
          </div>
        </div>
      );
    }
    return null;
  }

  const lowered = firstMedia.toLowerCase();
  if (post.category === "video") {
    return (
      <div className="px-4 pb-3">
        <div className="rounded-lg overflow-hidden border border-border/30 aspect-[16/10] bg-black/60">
          <video
            src={resolveMediaUrl(firstMedia)}
            controls
            preload="metadata"
            playsInline
            loading="lazy"
            className="w-full h-full object-contain"
          />
        </div>
      </div>
    );
  }
  if (post.category === "audio") {
    return (
      <div className="px-4 pb-3">
        <div className="rounded-lg border border-border/30 bg-surface-elevated p-4">
          <div className="flex items-center gap-3 mb-3">
            <MusicNote className="w-6 h-6 text-warning shrink-0" />
            <span className="text-sm font-medium truncate">{post.title}</span>
          </div>
          <audio src={resolveMediaUrl(firstMedia)} controls preload="none" className="w-full h-9" />
        </div>
      </div>
    );
  }
  return (
    <div className="px-4 pb-3">
      <div className="rounded-lg overflow-hidden border border-border/30 bg-surface-elevated">
        <img
          src={resolveMediaUrl(firstMedia)}
          alt={post.title}
          loading="lazy"
          className="w-full h-auto max-h-[22rem] sm:max-h-[25rem] object-contain"
        />
      </div>
    </div>
  );
}

function PostArticle({ post, onView }) {
  const ref = useRef(null);
  const viewed = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !viewed.current) {
          viewed.current = true;
          onView(post.id);
        }
      },
      { threshold: 0.5 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [post.id, onView]);

  const badge = CATEGORY_BADGES[post.category] || CATEGORY_BADGES.text;

  const handleShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const url = `${window.location.origin}/showcase/post/${post.slug || post.id}`;
    try {
      await apiClient.post(`/posts/${post.id}/share`).catch(() => {});
      if (navigator.share) {
        await navigator.share({ title: post.title, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied");
      }
    } catch (_) {}
  };

  return (
    <article
      ref={ref}
      className="bg-surface rounded-xl border border-border/40 overflow-hidden hover:border-primary/30 transition-colors"
    >
      <div className="p-4 pb-3 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-sm font-bold text-background shrink-0">
          {post.author_name?.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold truncate">{post.author_name}</p>
          <p className="text-xs text-muted-foreground">{formatDate(post.created_at)}</p>
        </div>
        <span className={`text-xs px-2 py-1 rounded-full font-semibold ${badge.className}`}>{badge.label}</span>
      </div>

      <Link to={`/showcase/post/${post.slug || post.id}`}>
        <div className="px-4 pb-3">
          <h3 className="font-heading font-semibold text-lg mb-1 hover:text-primary transition-colors">
            {post.title}
          </h3>
          {post.is_pinned && (
            <span className="inline-flex items-center gap-1 text-xs text-warning mr-2">
              <PushPin weight="fill" className="w-3.5 h-3.5" /> Pinned
            </span>
          )}
          {post.is_featured && !post.is_pinned && (
            <span className="inline-flex items-center gap-1 text-xs text-primary mr-2">
              <Star weight="fill" className="w-3.5 h-3.5" /> Featured
            </span>
          )}
          <p className="text-foreground/80 line-clamp-2 text-sm leading-relaxed">{post.description}</p>
        </div>

        {post.tags?.length > 0 && (
          <div className="px-4 pb-3 flex flex-wrap gap-2">
            {post.tags.slice(0, 5).map((tag) => (
              <span key={tag} className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs">
                #{tag}
              </span>
            ))}
          </div>
        )}

        <MediaPreview post={post} />
      </Link>

      <div className="px-4 py-3 border-t border-border/40 flex items-center gap-4 sm:gap-5 text-muted-foreground">
        <div className="flex items-center gap-2">
          <Heart className="w-5 h-5" />
          <span className="text-sm">{post.likes_count}</span>
        </div>
        <div className="flex items-center gap-2">
          <ChatCircle className="w-5 h-5" />
          <span className="text-sm">{post.comments_count}</span>
        </div>
        <div className="hidden sm:flex items-center gap-2">
          <Eye className="w-5 h-5" />
          <span className="text-sm">{post.views_count ?? 0}</span>
        </div>
        <button
          onClick={handleShare}
          className="ml-auto flex items-center gap-1.5 text-xs hover:text-primary transition-colors"
          title="Share post"
        >
          <ShareNetwork className="w-4 h-4" />
          Share
        </button>
      </div>
    </article>
  );
}

export default function PublicFeedPage() {
  const [posts, setPosts] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedTag, setSelectedTag] = useState(null);
  const [mediaType, setMediaType] = useState("all");
  const topRef = useRef(null);
  const sentinelRef = useRef(null);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const buildParams = useCallback(
    (nextCursor) => {
      const params = { limit: PAGE_SIZE };
      if (nextCursor) params.cursor = nextCursor;
      if (selectedTag) params.tag = selectedTag;
      if (debouncedSearch.trim()) params.q = debouncedSearch.trim();
      if (mediaType !== "all") params.media_type = mediaType;
      return params;
    },
    [debouncedSearch, selectedTag, mediaType]
  );

  // Initial / filter-change load
  useEffect(() => {
    let cancelled = false;
    setInitialLoading(true);
    setError(false);
    apiClient
      .get("/posts/showcase/feed", { params: buildParams(null) })
      .then((response) => {
        if (cancelled) return;
        const data = response.data;
        setPosts(data.posts || []);
        setTotal(data.total || 0);
        setCursor(data.next_cursor);
        setHasMore(Boolean(data.has_more));
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          toast.error("Failed to load public feed");
        }
      })
      .finally(() => {
        if (!cancelled) setInitialLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [buildParams]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || initialLoading || !cursor) return;
    setLoadingMore(true);
    try {
      const response = await apiClient.get("/posts/showcase/feed", { params: buildParams(cursor) });
      const data = response.data;
      setPosts((prev) => [...prev, ...(data.posts || [])]);
      setCursor(data.next_cursor);
      setHasMore(Boolean(data.has_more));
    } catch (_) {
      toast.error("Failed to load more posts");
    } finally {
      setLoadingMore(false);
    }
  }, [buildParams, cursor, hasMore, initialLoading, loadingMore]);

  // Infinite scroll observer
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadMore();
      },
      { rootMargin: "600px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore]);

  const handleView = useCallback(async (postId) => {
    try {
      await apiClient.post(`/posts/${postId}/view`).catch(() => {});
    } catch (_) {}
  }, []);

  const allTags = [...new Set(posts.flatMap((p) => p.tags))].slice(0, 12);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 border-b border-border/40 bg-background/90 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center gap-3 sm:gap-4">
          <Link to="/feed/public" className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-primary/20 border border-primary/50 flex items-center justify-center">
              <img src="/favicon/favicon.ico" alt="DIIT logo" className="w-7 h-7 rounded-sm object-contain" />
            </div>
            <div>
              <p className="font-heading font-semibold leading-tight">DIIT Showcase</p>
              <p className="text-xs text-muted-foreground leading-tight">Student projects wall</p>
            </div>
          </Link>

          <div className="flex-1 min-w-[220px] max-w-md mx-0 sm:mx-2 hidden sm:block">
            <div className="relative">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search projects, tags, students..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-10 pl-10 pr-4 bg-surface border border-border rounded-full text-sm focus:outline-none focus:border-primary transition-colors"
              />
            </div>
          </div>

          <div className="flex w-full sm:w-auto flex-wrap items-center justify-end gap-2">
            <Button asChild variant="outline" className="rounded-full" data-testid="public-feed-leaderboard-link">
              <Link to="/leaderboard/public">
                <Trophy className="w-4 h-4 mr-2" />
                Leaderboard
              </Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full" data-testid="public-feed-login-link">
              <Link to="/login">Login</Link>
            </Button>
            <Button asChild className="rounded-full" data-testid="public-feed-register-link">
              <Link to="/register">Register</Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="max-w-3xl mx-auto" ref={topRef}>
          {/* Mobile Search */}
          <div className="sm:hidden mb-4">
            <div className="relative">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search projects, tags, students..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-10 pl-10 pr-4 bg-surface border border-border rounded-full text-sm focus:outline-none focus:border-primary transition-colors"
              />
            </div>
          </div>

          {/* Tags */}
          {allTags.length > 0 && (
            <div className="mb-5 flex flex-wrap gap-2">
              {allTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    selectedTag === tag
                      ? "bg-primary text-primary-foreground"
                      : "bg-surface text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid={`public-tag-${tag}`}
                >
                  #{tag}
                </button>
              ))}
            </div>
          )}

          {/* Filters + Stats */}
          <div className="mb-5 flex flex-wrap gap-2 items-center justify-between">
            <div className="flex flex-wrap gap-2">
              <select
                value={mediaType}
                onChange={(e) => setMediaType(e.target.value)}
                className="h-9 px-3 rounded-full bg-surface border border-border text-sm"
                data-testid="public-feed-media-filter"
              >
                <option value="all">All Media</option>
                <option value="image">Images</option>
                <option value="video">Videos</option>
                <option value="audio">Audio</option>
              </select>
            </div>
            {total > 0 && (
              <p className="text-xs text-muted-foreground">{total} post{total !== 1 ? "s" : ""}</p>
            )}
          </div>

          {/* Posts */}
          {initialLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-surface rounded-xl p-6 animate-pulse">
                  <div className="w-full h-56 bg-surface-elevated rounded-lg mb-4" />
                  <div className="w-2/3 h-4 bg-surface-elevated rounded mb-2" />
                  <div className="w-1/2 h-4 bg-surface-elevated rounded" />
                </div>
              ))}
            </div>
          ) : error && posts.length === 0 ? (
            <div className="text-center py-16">
              <h3 className="font-heading text-xl font-semibold mb-2">Something went wrong</h3>
              <p className="text-muted-foreground mb-5">We couldn't load the feed. Please try again.</p>
              <Button className="rounded-full" onClick={() => window.location.reload()}>Retry</Button>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-surface flex items-center justify-center">
                <Code className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="font-heading text-xl font-semibold mb-2">No public posts yet</h3>
              <p className="text-muted-foreground mb-5">
                Visitors can explore student work here once projects are published.
              </p>
              <Button asChild className="rounded-full">
                <Link to="/register">Create an account</Link>
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {posts.map((post) => (
                  <PostArticle key={post.id} post={post} onView={handleView} />
                ))}
              </div>

              {/* Infinite scroll sentinel + states */}
              <div ref={sentinelRef} aria-hidden="true" style={{ height: 1 }} />
              {loadingMore && (
                <div className="mt-4 space-y-4">
                  {[1, 2].map((i) => (
                    <div key={i} className="bg-surface rounded-xl p-6 animate-pulse">
                      <div className="w-full h-40 bg-surface-elevated rounded-lg" />
                    </div>
                  ))}
                </div>
              )}
              {!hasMore && posts.length > 0 && (
                <p className="text-center text-sm text-muted-foreground py-8">
                  You've reached the end · {posts.length} posts shown
                </p>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
